import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { CreditCard, Zap } from 'lucide-react';
import KPCardHero from './KPCardHero';
import KPCardServices from './KPCardServices';
import KPCardLocation from './KPCardLocation';
import KPCardSmartParking from './KPCardSmartParking';
import KPCardContact from './KPCardContact';
import './KPCard.css';

const RechargeSection = styled.section`
  background: linear-gradient(135deg, #7F1B1C 0%, #640003 100%);
  padding: 60px 20px;
  text-align: center;
`;

const RechargeContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const RechargeTitle = styled.h2`
  color: white;
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const RechargeSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.25rem;
  margin-bottom: 32px;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const RechargeButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
`;

const RechargeButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  background: white;
  color: #C31321;
  padding: 18px 36px;
  border: none;
  border-radius: 50px;
  font-size: 1.125rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
    background: #FFD700;
  }

  @media (max-width: 768px) {
    padding: 14px 28px;
    font-size: 1rem;
  }
`;

const FeaturesList = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-top: 48px;
  padding: 0 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const FeatureCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 24px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const FeatureIcon = styled.div`
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
`;

const FeatureTitle = styled.h3`
  color: white;
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 8px;
`;

const FeatureText = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.875rem;
  line-height: 1.5;
`;

const KPCard = () => {
  const navigate = useNavigate();

  const handleRechargeClick = () => {
    navigate('/kp-card/recharge');
  };

  return (
    <div className="kp-card-page">
      <KPCardHero />
      
      <RechargeSection>
        <RechargeContainer>
          <RechargeTitle>Recharge Your KP Card</RechargeTitle>
          <RechargeSubtitle>
            Top up your KP Card instantly and enjoy seamless parking payments across all KeyPlot facilities
          </RechargeSubtitle>
          
          <RechargeButtonGroup>
            <RechargeButton onClick={handleRechargeClick}>
              <CreditCard size={24} />
              Recharge Now
            </RechargeButton>
          </RechargeButtonGroup>

          <FeaturesList>
            <FeatureCard>
              <FeatureIcon>
                <Zap size={24} color="white" />
              </FeatureIcon>
              <FeatureTitle>Instant Top-Up</FeatureTitle>
              <FeatureText>
                Your balance updates immediately after successful payment
              </FeatureText>
            </FeatureCard>

            <FeatureCard>
              <FeatureIcon>
                <CreditCard size={24} color="white" />
              </FeatureIcon>
              <FeatureTitle>Secure Payments</FeatureTitle>
              <FeatureText>
                Protected by 3D Secure and encrypted transactions
              </FeatureText>
            </FeatureCard>

            <FeatureCard>
              <FeatureIcon>
                <CheckCircle size={24} color="white" />
              </FeatureIcon>
              <FeatureTitle>Multiple Options</FeatureTitle>
              <FeatureText>
                Choose from predefined amounts or enter custom value
              </FeatureText>
            </FeatureCard>
          </FeaturesList>
        </RechargeContainer>
      </RechargeSection>

      <KPCardServices />
      <KPCardLocation />
      <KPCardSmartParking />
      <KPCardContact />
    </div>
  );
};

// Import CheckCircle at the top with other icons
const CheckCircle = ({ size, color }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

export default KPCard; 